// PublicSnapshotStore — read-only store for spectators (anyone not vol+).
//
// Instead of opening ~15 realtime channels and refetching the whole DB on every
// edit (which is what makes 100+ viewers expensive), spectators poll the
// edge-cached /api/public-state snapshot every few seconds. The endpoint is
// Cloudflare-cached, so viewer count never touches the DB connection pool —
// origin sees ~one read per cache window regardless of how many are watching.
//
// dispatch is a no-op: spectators have no write surface anyway. On a failed poll
// the store keeps its last-good snapshot (a blip never blanks the board) and
// flips realtime status to 'degraded'.

import type { Action, AppState } from '../types';
import type { Store, SystemStatus } from './Store';
import type { AssembledState } from './assembleState';
import { initialState as baseState } from '../state/reducer';

const DEFAULT_POLL_MS = 8000;

export interface PublicSnapshotStoreOptions {
  url?: string;
  pollMs?: number;
  /** Injectable for tests. */
  fetchFn?: typeof fetch;
  /** Offseason: seed from a committed final-season snapshot and do NOT poll —
   *  the archive is frozen, so there's no live endpoint to hit. */
  frozen?: AssembledState;
}

export function createPublicSnapshotStore(opts: PublicSnapshotStoreOptions = {}): Store {
  const url = opts.url ?? '/api/public-state';
  const pollMs = opts.pollMs ?? DEFAULT_POLL_MS;
  const fetchFn = opts.fetchFn ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));

  // Seed with the mock base so the non-public AppState keys (activeInspection,
  // pendingWarning, …) have sane defaults; the first poll overwrites every
  // public slice. Brief mock flash on first paint, same as SupabaseStore.
  // Blank the superuser-only Access Control slices — never relevant to the
  // public snapshot, and the mock seed shouldn't ship to anon clients.
  let state: AppState = opts.frozen
    ? { ...baseState, ...opts.frozen, accessRoster: [], volunteerEmails: [] }
    : { ...baseState, accessRoster: [], volunteerEmails: [] };
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((l) => l());

  let systemStatus: SystemStatus = opts.frozen
    ? { realtime: 'connected', schemaVersionMismatch: null, lastError: null }
    : { realtime: 'connecting', schemaVersionMismatch: null, lastError: null };
  const updateStatus = (patch: Partial<SystemStatus>) => { systemStatus = { ...systemStatus, ...patch }; notify(); };

  let timer: ReturnType<typeof setInterval> | null = null;
  let disposed = false;

  const poll = async () => {
    try {
      const res = await fetchFn(url, { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const snapshot = (await res.json()) as AssembledState;
      if (disposed) return;
      // Public slices over the base; UI-local keys keep their defaults.
      state = { ...baseState, ...snapshot };
      systemStatus = { ...systemStatus, realtime: 'connected', lastError: null };
      notify();
    } catch (err) {
      if (disposed) return;
      // Keep the last good snapshot; surface the failure as degraded.
      const message = err instanceof Error ? err.message : String(err);
      updateStatus({ realtime: 'degraded', lastError: { actionType: 'public-snapshot', message, at: Date.now() } });
    }
  };

  // Frozen (offseason): the snapshot is already seeded above; skip all polling.
  const onVisible = () => { if (typeof document !== 'undefined' && !document.hidden) void poll(); };
  if (!opts.frozen) {
    void poll();
    timer = setInterval(() => { void poll(); }, pollMs);
    // Catch up fast when a backgrounded tab returns to the foreground.
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
  }

  return {
    kind: 'snapshot',
    getState: () => state,
    // Read-only: spectators can't write. Swallow rather than throw so any
    // accidental dispatch (e.g. a shared component) is a harmless no-op.
    dispatch: (_action: Action) => { void _action; },
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    getSystemStatus: () => systemStatus,
    clearLastError: () => { if (systemStatus.lastError) updateStatus({ lastError: null }); },
    dispose: () => {
      disposed = true;
      if (timer != null) { clearInterval(timer); timer = null; }
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    },
  };
}
